import {useCallback, useEffect, useState} from 'react';
import {useHistory} from '../useHistory';
import {useIsInitialLocationCallback} from '../useIsInitialLocation';
import {useCompleteLocation} from '../react-router';

const canGoBack = ({
  historyLength,
  isInitialLocation,
  pageKey,
}: {
  historyLength: number;
  isInitialLocation: boolean;
  pageKey: string | undefined;
}) => {
  /*
   * the check for pageKey is here to reliably check whether ANY router navigation has taken place in the current session
   * useIsInitialLocation() is NOT covering the location state but the pageKey is always undefined
   * until a router navigation occurs
   */
  const isExternalReferrer =
    historyLength > 1 && isInitialLocation && pageKey === undefined;
  const isNewTab = historyLength === undefined || historyLength < 2;

  return !isNewTab && !isExternalReferrer;
};

export const useCanGoBack_PRIVATE = () => {
  const history = useHistory();
  const pageKey = useCompleteLocation().key;

  const [historyBackPossible, changeHistoryBackPossible] = useState(false);

  useEffect(() => {
    return history.listen(
      () => {
        // This gets triggered only when an actual route change happens. (excluding UI updates and URL replaces like in ADP color switching)
        changeHistoryBackPossible(true);
      },
      {disableUiOrUrlsReplaceUpdates: true},
    );
  }, [history]);
  /**
   * when `historyBackPossible` is true, it doesnt mean the user can always "go back".
   * Go back is only possible until pageKey becomes undefined. pageKey is undefined only on the direct entry case.
   */
  return historyBackPossible ? Boolean(pageKey) : false;
};

export const useCanGoBackCallback = () => {
  const history = useHistory();
  const isInitialLocation = useIsInitialLocationCallback();

  return useCallback(() => {
    return canGoBack({
      historyLength: history.length,
      pageKey: history.location.key,
      isInitialLocation: isInitialLocation(),
    });
  }, [history, isInitialLocation]);
};
